/**
 * EVENTSFLO-FINANCIAL-TAX-RULES-MATRIX-ADMIN-UI-V1
 *
 * Admin page for the (ClientTaxTreatment × LineTaxClass) → VAT decision
 * matrix. Lazy-seeds defaults on first load (server-side). Supports:
 *   - View existing rules.
 *   - Inline edit (vat_rate, vat_label, reverse_charge, invoice_note,
 *     requires_valid_vat_number, requires_manual_review, active, priority).
 *   - Add a new rule.
 *   - Deactivate (PATCH active=false — soft, non-destructive).
 *   - Delete (DELETE — destructive, requires explicit confirm).
 *   - Re-seed platform defaults.
 *
 * Backend logic (tax evaluator, snapshots, PDF rendering) is unchanged.
 * Manual-review banner is static (no diagnostic endpoint yet).
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  listFinancialTaxRules,
  createFinancialTaxRule,
  updateFinancialTaxRule,
  deleteFinancialTaxRule,
  seedFinancialTaxRulesDefaults,
  extractErrorMessage,
} from "../lib/api";
import {
  CLIENT_TAX_TREATMENTS,
  LINE_TAX_CLASSES,
  labelFor,
} from "../lib/taxEnums";
import {
  PageHeader,
  LoadingSpinner,
  EmptyState,
} from "../components/shared/DataComponents";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
// EVENTSFLO-FINANCIAL-TAX-RULES-LINE-LEVEL-AUDIT-VIEW-V1
import { HelpPopover } from "../components/shared/HelpPopover";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Save,
  X,
  Pencil,
  Trash2,
  RotateCcw,
  AlertTriangle,
} from "lucide-react";

const EMPTY_NEW_RULE = {
  client_tax_treatment: "domestic_tva",
  line_tax_class: "crew_service",
  vat_rate: 0.2,
  vat_label: "TVA 20%",
  reverse_charge_applied: false,
  invoice_note: "",
  requires_valid_vat_number: false,
  requires_manual_review: false,
  active: true,
  priority: 100,
};

export default function FinancialTaxRulesPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editingDraft, setEditingDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newRule, setNewRule] = useState(EMPTY_NEW_RULE);
  const [adding, setAdding] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const res = await listFinancialTaxRules();
      setRules(res.data?.rules || []);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Rules carrying requires_manual_review=true are the trigger for the
  // banner. We compute it from the current matrix every render.
  const anyManualReview = useMemo(
    () => rules.some((r) => r.requires_manual_review && r.active),
    [rules],
  );

  const startEdit = (rule) => {
    setEditingId(rule.id);
    setEditingDraft({
      vat_rate: rule.vat_rate,
      vat_label: rule.vat_label || "",
      reverse_charge_applied: !!rule.reverse_charge_applied,
      invoice_note: rule.invoice_note || "",
      requires_valid_vat_number: !!rule.requires_valid_vat_number,
      requires_manual_review: !!rule.requires_manual_review,
      active: rule.active !== false,
      priority: rule.priority ?? 100,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingDraft(null);
  };

  const saveEdit = async (rule) => {
    if (!editingDraft) return;
    try {
      setSaving(true);
      const payload = {
        vat_rate: Number(editingDraft.vat_rate),
        vat_label: editingDraft.vat_label || null,
        reverse_charge_applied: !!editingDraft.reverse_charge_applied,
        invoice_note: editingDraft.invoice_note || null,
        requires_valid_vat_number: !!editingDraft.requires_valid_vat_number,
        requires_manual_review: !!editingDraft.requires_manual_review,
        active: editingDraft.active !== false,
        priority: Number(editingDraft.priority) || 100,
      };
      await updateFinancialTaxRule(rule.id, payload);
      toast.success("Tax rule updated");
      cancelEdit();
      await load();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const deactivateRule = async (rule) => {
    try {
      setSaving(true);
      await updateFinancialTaxRule(rule.id, { active: false });
      toast.success("Tax rule deactivated");
      await load();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const reactivateRule = async (rule) => {
    try {
      setSaving(true);
      await updateFinancialTaxRule(rule.id, { active: true });
      toast.success("Tax rule activated");
      await load();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteCandidate) return;
    try {
      setDeleting(true);
      await deleteFinancialTaxRule(deleteCandidate.id);
      toast.success("Tax rule deleted");
      setDeleteCandidate(null);
      await load();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  const handleSeedDefaults = async () => {
    try {
      setSeeding(true);
      const res = await seedFinancialTaxRulesDefaults();
      const inserted = res.data?.inserted ?? 0;
      toast.success(
        inserted > 0
          ? `Seeded ${inserted} default tax rule(s)`
          : "Defaults already present — nothing to seed",
      );
      await load();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setSeeding(false);
    }
  };

  const handleCreateRule = async () => {
    try {
      setAdding(true);
      const payload = {
        client_tax_treatment: newRule.client_tax_treatment,
        line_tax_class: newRule.line_tax_class,
        vat_rate: Number(newRule.vat_rate),
        vat_label: newRule.vat_label || "TVA",
        reverse_charge_applied: !!newRule.reverse_charge_applied,
        invoice_note: newRule.invoice_note || null,
        requires_valid_vat_number: !!newRule.requires_valid_vat_number,
        requires_manual_review: !!newRule.requires_manual_review,
        active: newRule.active !== false,
        priority: Number(newRule.priority) || 100,
      };
      await createFinancialTaxRule(payload);
      toast.success("Tax rule created");
      setAddDialogOpen(false);
      setNewRule(EMPTY_NEW_RULE);
      await load();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setAdding(false);
    }
  };

  const formatRate = (r) =>
    r == null || Number.isNaN(Number(r))
      ? "—"
      : `${(Number(r) * 100).toFixed(2).replace(/\.00$/, "")}%`;

  return (
    <div className="space-y-4" data-testid="financial-tax-rules-page">
      <PageHeader
        title="Tax Rules Matrix"
        subtitle="Admin-editable matrix mapping (client tax treatment × line tax class) → VAT rate, reverse-charge eligibility and labels."
        actions={
          <div className="flex gap-2 items-center">
            {/* EVENTSFLO-FINANCIAL-TAX-RULES-LINE-LEVEL-AUDIT-VIEW-V1 */}
            <HelpPopover
              testId="tax-rules-matrix-help"
              title="How the Tax Rules Matrix works"
              align="end"
            >
              <p>
                The matrix decides the <strong>VAT / TVA treatment</strong> for
                every quote and invoice line by matching the client&apos;s
                <strong> Tax Treatment </strong> with the task&apos;s
                <strong> Line Tax Class</strong>.
              </p>
              <p>Rules are fully editable — priority controls tie-breakers.</p>
              <p>
                Editing a rule does <strong>not</strong> change quotes /
                invoices that have already been generated — every line
                captures its own snapshot at generation time.
              </p>
              <p>
                A <strong>Manual review</strong> rule means finance / admin
                should double-check the affected line before issuing the
                document.
              </p>
              <p>
                <strong>UK VAT-registered clients</strong> normally use the{' '}
                <code>non_eu_b2b_services</code> treatment (post-Brexit UK is a
                third country for VAT purposes) — the matrix produces{' '}
                <code>VAT 0% Non-EU</code> for crew labour and TVA 20% for
                rentals.
              </p>
            </HelpPopover>
            <Button
              variant="outline"
              onClick={() => navigate("/settings")}
              data-testid="tax-rules-back-button"
            >
              <ArrowLeft className="h-4 w-4 mr-1" /> Back to Settings
            </Button>
            <Button
              variant="outline"
              onClick={handleSeedDefaults}
              disabled={seeding}
              data-testid="tax-rule-seed-defaults-button"
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              {seeding ? "Seeding…" : "Seed defaults"}
            </Button>
            <Button
              onClick={() => {
                setNewRule(EMPTY_NEW_RULE);
                setAddDialogOpen(true);
              }}
              data-testid="tax-rule-add-button"
            >
              <Plus className="h-4 w-4 mr-1" /> Add rule
            </Button>
          </div>
        }
      />

      {anyManualReview && (
        <Alert
          variant="default"
          className="border-amber-500/40 bg-amber-500/10"
          data-testid="tax-rule-manual-review-warning"
        >
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Manual review rules are active</AlertTitle>
          <AlertDescription>
            One or more active tax rules are flagged as “Requires manual review”.
            Quotes or invoices using lines that match those rules should be
            checked by an accountant before issuing. Open a quote / invoice to
            inspect the affected lines.
          </AlertDescription>
        </Alert>
      )}

      {loading ? (
        <LoadingSpinner />
      ) : rules.length === 0 ? (
        <EmptyState
          icon={AlertTriangle}
          title="No tax rules found"
          description="Click ‘Seed defaults’ to populate the platform defaults."
        />
      ) : (
        <div className="border border-white/10 rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Client Tax Treatment</TableHead>
                <TableHead>Line Tax Class</TableHead>
                <TableHead>VAT Rate</TableHead>
                <TableHead>VAT Label</TableHead>
                <TableHead>RC</TableHead>
                <TableHead>Req. VAT #</TableHead>
                <TableHead>Manual review</TableHead>
                <TableHead>Active</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Invoice Note</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rules.map((rule) => {
                const isEditing = editingId === rule.id;
                return (
                  <TableRow
                    key={rule.id}
                    data-testid={`tax-rule-row-${rule.id}`}
                    className={!rule.active ? "opacity-60" : ""}
                  >
                    <TableCell className="font-mono text-xs">
                      {labelFor(CLIENT_TAX_TREATMENTS, rule.client_tax_treatment)}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {labelFor(LINE_TAX_CLASSES, rule.line_tax_class)}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Input
                          type="number"
                          step="0.001"
                          min="0"
                          max="1"
                          value={editingDraft.vat_rate}
                          onChange={(e) =>
                            setEditingDraft({ ...editingDraft, vat_rate: e.target.value })
                          }
                          className="w-24"
                        />
                      ) : (
                        formatRate(rule.vat_rate)
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Input
                          value={editingDraft.vat_label}
                          onChange={(e) =>
                            setEditingDraft({ ...editingDraft, vat_label: e.target.value })
                          }
                          className="w-32"
                        />
                      ) : (
                        rule.vat_label || "—"
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Switch
                          checked={!!editingDraft.reverse_charge_applied}
                          onCheckedChange={(v) =>
                            setEditingDraft({ ...editingDraft, reverse_charge_applied: v })
                          }
                        />
                      ) : rule.reverse_charge_applied ? (
                        <Badge variant="outline" className="border-amber-500/50 text-amber-600">
                          RC
                        </Badge>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Switch
                          checked={!!editingDraft.requires_valid_vat_number}
                          onCheckedChange={(v) =>
                            setEditingDraft({ ...editingDraft, requires_valid_vat_number: v })
                          }
                        />
                      ) : rule.requires_valid_vat_number ? (
                        "Yes"
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Switch
                          checked={!!editingDraft.requires_manual_review}
                          onCheckedChange={(v) =>
                            setEditingDraft({ ...editingDraft, requires_manual_review: v })
                          }
                        />
                      ) : rule.requires_manual_review ? (
                        <Badge variant="outline" className="border-amber-500/50 text-amber-600">
                          Review
                        </Badge>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Switch
                          checked={editingDraft.active !== false}
                          onCheckedChange={(v) =>
                            setEditingDraft({ ...editingDraft, active: v })
                          }
                        />
                      ) : rule.active ? (
                        <Badge variant="default">Active</Badge>
                      ) : (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Input
                          type="number"
                          value={editingDraft.priority}
                          onChange={(e) =>
                            setEditingDraft({ ...editingDraft, priority: e.target.value })
                          }
                          className="w-20"
                        />
                      ) : (
                        rule.priority ?? "—"
                      )}
                    </TableCell>
                    <TableCell className="max-w-[20ch] truncate" title={rule.invoice_note || ""}>
                      {isEditing ? (
                        <Input
                          value={editingDraft.invoice_note}
                          onChange={(e) =>
                            setEditingDraft({ ...editingDraft, invoice_note: e.target.value })
                          }
                          placeholder="Legal note rendered on PDF"
                        />
                      ) : (
                        rule.invoice_note || "—"
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {isEditing ? (
                        <div className="flex gap-1 justify-end">
                          <Button
                            size="sm"
                            onClick={() => saveEdit(rule)}
                            disabled={saving}
                            data-testid={`tax-rule-save-${rule.id}`}
                          >
                            <Save className="h-3 w-3 mr-1" /> Save
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={cancelEdit}
                            data-testid={`tax-rule-cancel-${rule.id}`}
                          >
                            <X className="h-3 w-3 mr-1" /> Cancel
                          </Button>
                        </div>
                      ) : (
                        <div className="flex gap-1 justify-end">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => startEdit(rule)}
                            data-testid={`tax-rule-edit-${rule.id}`}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                          {rule.active ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => deactivateRule(rule)}
                              disabled={saving}
                              title="Deactivate (non-destructive)"
                              data-testid={`tax-rule-deactivate-${rule.id}`}
                            >
                              Deactivate
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => reactivateRule(rule)}
                              disabled={saving}
                              data-testid={`tax-rule-reactivate-${rule.id}`}
                            >
                              Activate
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-500 hover:text-red-600"
                            onClick={() => setDeleteCandidate(rule)}
                            title="Delete permanently"
                            data-testid={`tax-rule-delete-${rule.id}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Add Rule Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-xl" data-testid="tax-rule-add-dialog">
          <DialogHeader>
            <DialogTitle>Add Tax Rule</DialogTitle>
            <DialogDescription>
              Backend payload values must match the enum strings exactly — they
              are taken from /app/backend/models.py.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Client Tax Treatment</Label>
              <Select
                value={newRule.client_tax_treatment}
                onValueChange={(v) =>
                  setNewRule({ ...newRule, client_tax_treatment: v })
                }
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CLIENT_TAX_TREATMENTS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Line Tax Class</Label>
              <Select
                value={newRule.line_tax_class}
                onValueChange={(v) =>
                  setNewRule({ ...newRule, line_tax_class: v })
                }
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LINE_TAX_CLASSES.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>VAT Rate (decimal, e.g. 0.20)</Label>
              <Input
                type="number"
                step="0.001"
                min="0"
                max="1"
                value={newRule.vat_rate}
                onChange={(e) =>
                  setNewRule({ ...newRule, vat_rate: e.target.value })
                }
              />
            </div>
            <div>
              <Label>VAT Label</Label>
              <Input
                value={newRule.vat_label}
                onChange={(e) =>
                  setNewRule({ ...newRule, vat_label: e.target.value })
                }
                placeholder="TVA 20% / VAT 0% RC"
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={newRule.reverse_charge_applied}
                onCheckedChange={(v) =>
                  setNewRule({ ...newRule, reverse_charge_applied: v })
                }
              />
              <Label>Reverse Charge</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={newRule.requires_valid_vat_number}
                onCheckedChange={(v) =>
                  setNewRule({ ...newRule, requires_valid_vat_number: v })
                }
              />
              <Label>Requires Valid VAT Number</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={newRule.requires_manual_review}
                onCheckedChange={(v) =>
                  setNewRule({ ...newRule, requires_manual_review: v })
                }
              />
              <Label>Requires Manual Review</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={newRule.active}
                onCheckedChange={(v) =>
                  setNewRule({ ...newRule, active: v })
                }
              />
              <Label>Active</Label>
            </div>
            <div>
              <Label>Priority</Label>
              <Input
                type="number"
                value={newRule.priority}
                onChange={(e) =>
                  setNewRule({ ...newRule, priority: e.target.value })
                }
              />
            </div>
            <div className="col-span-2">
              <Label>Invoice Note</Label>
              <Textarea
                rows={2}
                value={newRule.invoice_note}
                onChange={(e) =>
                  setNewRule({ ...newRule, invoice_note: e.target.value })
                }
                placeholder="e.g. Reverse charge under Art. 196 EU VAT Directive."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleCreateRule}
              disabled={adding}
              data-testid="tax-rule-add-confirm"
            >
              {adding ? "Adding…" : "Add rule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Destructive Delete Confirmation */}
      <AlertDialog open={!!deleteCandidate} onOpenChange={(v) => !v && setDeleteCandidate(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete tax rule permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              Hard delete removes the rule from the matrix. New quotes/invoices
              will fall back to the legacy line-level tax snapshot for the
              affected (treatment × class). Previously issued documents are
              unaffected (snapshots preserve their original VAT decision).
              Consider <strong>Deactivate</strong> instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? "Deleting…" : "Delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
